import { httpBase } from "./backend";
/**
 * The screener's hover card: one symbol's last 60 daily bars (`/sym/spark`).
 *
 * Read degrades: a failure is an empty card, never a throw, since a hover that
 * shows nothing is a smaller failure than one that breaks the grid. Held for
 * five minutes per symbol in the page, so sweeping back over a row costs
 * nothing; the server's file changes once a day.
 */

const URL_BASE =
  httpBase(
    import.meta.env.VITE_QV_IND_URL as string | undefined,
    import.meta.env.VITE_QV_SET_URL as string | undefined,
  ) || "";

export interface SparkBar {
  date: string;
  close: number;
  volume: number | null;
}

export interface SparkCard {
  symbol: string;
  bars: readonly SparkBar[];
  close?: number;
  /** Over the window: last close against the first. */
  changePct?: number | null;
  high?: number;
  low?: number;
}

const HOLD_MS = 5 * 60_000;
const cache = new Map<string, { at: number; card: Promise<SparkCard> }>();

export function fetchSpark(symbol: string): Promise<SparkCard> {
  const hit = cache.get(symbol);
  if (hit && Date.now() - hit.at < HOLD_MS) return hit.card;
  const card = load(symbol);
  cache.set(symbol, { at: Date.now(), card });
  return card;
}

async function load(symbol: string): Promise<SparkCard> {
  const empty: SparkCard = { symbol, bars: [] };
  if (!URL_BASE) return empty;
  try {
    const res = await fetch(`${URL_BASE.replace(/\/$/, "")}/sym/spark/${encodeURIComponent(symbol)}`);
    if (!res.ok) return empty;
    const body = (await res.json()) as Partial<SparkCard>;
    return {
      symbol,
      bars: Array.isArray(body.bars) ? body.bars : [],
      close: body.close,
      changePct: body.changePct,
      high: body.high,
      low: body.low,
    };
  } catch {
    cache.delete(symbol);
    return empty;
  }
}
